import { useRef, useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SubPageHeader from '@/components/layout/SubPageHeader';
import SubPageShell from '@/components/layout/SubPageShell';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import { InfoField } from '@/components/base/InfoField';
import SectionHeading from '@/components/base/SectionHeading';

/** 建物中心（東奥日報社八戸ビル）付近の座標。新規タブの検索用に正式住所付き名を併用 */
const OFFICE_PLACE = '東奥日報社八戸ビル 青森県八戸市北白山台5丁目2-5' as const;
const OFFICE_PLACE_GEO = { lat: 40.485256, lng: 141.463579 } as const;
const { lat: officeLat, lng: officeLng } = OFFICE_PLACE_GEO;
/** 座標の q のみ（ll + 施設名 q を併用すると赤ピンが重なる／二重表示になりやすい） */
const mapEmbedUrl = `https://www.google.com/maps?q=${officeLat}%2C${officeLng}&z=18&hl=ja&output=embed`;
const mapOpenUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_PLACE)}`;

/** 埋め込み地図左上の自社案内（Google 枠内パネルに頼らない） */
function MapLocationCard() {
  return (
    <aside
      className="absolute left-2 top-2 z-20 max-w-[min(calc(100%-1rem),15.5rem)] rounded border border-slate-200/90 bg-white/95 p-2.5 pl-2.5 pr-2 shadow-md backdrop-blur-[2px] sm:left-3 sm:top-3 sm:p-3"
      role="region"
      aria-label="株式会社キャデック 所在地"
    >
      <p
        className="text-[14px] font-bold leading-snug text-slate-900"
        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
      >
        株式会社キャデック
      </p>
      <p
        className="mt-1.5 text-[12px] leading-[1.55] text-slate-600"
        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
      >
        〒039-1114
        <br />
        青森県八戸市北白山台5丁目2-5
        <br />
        東奥日報社八戸ビル5階
      </p>
    </aside>
  );
}

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function MapPage() {
  return (
    <SubPageShell>
      <Navbar />

      <SubPageHeader>
          <SectionHeading label="ACCESS" title="アクセス" as="h1" tone="dark" size="page" />
          <p
            className="mt-6 max-w-xl"
            style={{ fontSize: '17px', color: '#FFFFFF', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.0' }}
          >
            青森県八戸市北白山台にございます。お気軽にお越しください。
          </p>
      </SubPageHeader>

      {/* Map & Info */}
      <SubPageSection>
          <FadeIn>
            <SectionHeading label="LOCATION" title="所在地・アクセス" tone="dark" className="mb-14" />
          </FadeIn>

          <SubPageSurface className="p-8 md:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* Info */}
            <div className="lg:col-span-2">
              <FadeIn>
                <div className="space-y-11">
                  <InfoField label="COMPANY">
                    <p
                      className="text-[21px] font-bold leading-snug text-slate-900"
                      style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      株式会社キャデック
                    </p>
                    <p
                      className="mt-2 text-[15px] leading-normal text-slate-600"
                      style={{ fontFamily: '"Inter", sans-serif', letterSpacing: '0.04em' }}
                    >
                      Computer Added Development &amp; Engineering Co.,ltd.
                    </p>
                  </InfoField>

                  <InfoField label="ADDRESS">
                    <p style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                      〒039-1114
                      <br />
                      青森県八戸市北白山台5丁目2-5
                      <br />
                      東奥日報社八戸ビル5階
                    </p>
                  </InfoField>

                  <InfoField label="TEL / FAX">
                    <p
                      className="text-[19px] font-medium tabular-nums"
                      style={{ fontFamily: '"Inter", sans-serif', color: '#0f172a' }}
                    >
                      TEL 0178-51-8296
                      <br />
                      FAX 0178-51-8297
                    </p>
                  </InfoField>

                  <InfoField label="E-MAIL">
                    <a
                      href="mailto:cadec@cadec.co.jp"
                      className="inline-block text-[18px] transition-colors duration-200"
                      style={{ fontFamily: '"Inter", sans-serif', color: '#0f172a' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#B48C50'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#0f172a'; }}
                    >
                      cadec@cadec.co.jp
                    </a>
                  </InfoField>

                  <InfoField label="REGISTRATION">
                    <p style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
                      一級建築士事務所
                      <br />
                      青森県知事登録 第844号
                    </p>
                  </InfoField>
                </div>
              </FadeIn>
            </div>

            {/* 2D 地図 ＋ 外観・入口の写真（埋め込みはストリートビューではなく平面地図） */}
            <div className="lg:col-span-3 flex min-h-0 flex-col gap-6">
              <FadeIn delay={100}>
                <div
                  className="relative w-full overflow-hidden rounded-sm"
                  style={{
                    minHeight: 'min(45vh, 480px)',
                    height: '440px',
                    border: '1px solid #EBEBEB',
                    background: '#E8E6E0',
                  }}
                >
                  <iframe
                    className="relative z-0"
                    title="株式会社キャデック 所在地（地図）"
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '100%' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <MapLocationCard />
                </div>
                <div className="mt-1 flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p
                    className="w-full sm:w-auto text-left sm:text-right"
                    style={{ fontSize: '14px', color: '#4B5563', fontFamily: '"Noto Sans JP", sans-serif' }}
                  >
                    〒039-1114 青森県八戸市北白山台5丁目2-5 東奥日報社八戸ビル5階
                  </p>
                  <a
                    href={mapOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-sm underline-offset-2 transition-colors"
                    style={{ color: '#111111', fontFamily: '"Noto Sans JP", sans-serif' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#4B5563'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#111111'; }}
                  >
                    Google マップで開く
                  </a>
                </div>
              </FadeIn>

              <FadeIn delay={200}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-4">
                  <div className="flex flex-col gap-2">
                    <div className="w-full overflow-hidden" style={{ height: '180px', background: '#F0F0EE' }}>
                      <img
                        src="https://static.readdy.ai/image/94b1f1bd4fea3de955c74d2ea2c388ec/4670eed6d111c8ffdf975ba4bee89551.png"
                        alt="東奥日報社八戸ビル 外観"
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <p
                      className="text-center"
                      style={{ fontSize: '15px', color: '#4B5563', fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      東奥日報社八戸ビル 外観
                    </p>
                    <div className="mt-1 flex items-start gap-2">
                      <i className="ri-information-line mt-0.5 shrink-0" style={{ fontSize: '18px', color: '#4B5563' }} aria-hidden />
                      <p
                        style={{ fontSize: '14px', color: '#1A1A1A', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.75' }}
                      >
                        入口は正面向かって<strong>右側</strong>です
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="w-full overflow-hidden" style={{ height: '180px', background: '#F0F0EE' }}>
                      <img
                        src="https://static.readdy.ai/image/94b1f1bd4fea3de955c74d2ea2c388ec/d6793a0d0679a39ce11a1a67535bfad3.png"
                        alt="事務所入口"
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <p
                      className="text-center"
                      style={{ fontSize: '15px', color: '#4B5563', fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      事務所入口
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
          </SubPageSurface>
      </SubPageSection>

      <Footer />
    </SubPageShell>
  );
}
